import { useSyncExternalStore } from 'react';

/**
 * ADR-120 P8 step 7(b) — the zones this staff console can see, and how it reaches each one.
 *
 * ⭐⭐ THE FAN-OUT HAPPENS IN THIS BROWSER, NOT IN A LAMBDA, AND THAT IS A COMPLIANCE
 * REQUIREMENT RATHER THAN AN IMPLEMENTATION PREFERENCE.
 *
 * ADR-120 P8 step 7(b) was originally worded as "the staff API fans out to each zone's Lambda
 * and merges results". **Amendment 2 explicitly rejects that**: one zone's Lambda reading
 * another zone's customer records IS the cross-zone personal-data flow ADR-099 §D11 forbids,
 * and the amendment says so in as many words. What it permits instead is the distinction it
 * turns on — *"a staff operator's own browser reaching each zone directly with one
 * globally-valid identity is not a zone reading another zone's data."*
 *
 * So: N requests from one operator's browser, each to its own zone's admin API, each carrying
 * the SAME staff token — valid everywhere because every zone's authorizer trusts the one global
 * staff pool (Amendment 2). The records meet in the operator's browser, which is where the
 * operator was always allowed to see them. No zone ever holds another zone's data.
 *
 * ⚠ DO NOT "OPTIMISE" THIS INTO A SERVER-SIDE AGGREGATOR. It would read as a tidy-up and it
 * would be a residency breach.
 *
 * ── Why the zone list is build-time, when Stage 6 exists to remove build-time zone binding ──
 *
 * These are two different questions and only one of them is Stage 6's:
 *
 *   - *Which zone does THIS VISITOR belong to?* — per-visitor, unknowable at build time, and
 *     genuinely blocked on an open product decision (P6: what happens to a traveller whose
 *     home zone is not the nearest one). That is `/zone-config`, and it is not built here.
 *   - *Which zones EXIST?* — a platform fact that changes only when a zone is added, which is
 *     a deploy. Baking it at build time is honest, and it keeps this stage from depending on a
 *     decision that has not been made.
 *
 * Adding a third zone is one entry here plus one `export VITE_…` line in deploy.yml — the same
 * "adding a zone is adding an entry" shape `cdk/lib/zones.ts` has.
 */

/**
 * ADR-123 Decision 4 — the FULL zone table: every zone the platform has, whether or not this
 * bundle was given its address.
 *
 * ⭐ TWO LISTS, ON PURPOSE. `ZONE_TABLE` is what EXISTS; `ZONES` below is what this bundle can
 * REACH. They differ only in a misconfigured deploy — one shipped without the US admin-API
 * address — and that difference is exactly what the navigation-bar indicator has to be able to
 * state ("1 zone configured — expected 2"). A bundle that only knew its configured subset could
 * not tell a one-zone platform from a broken two-zone one, which is how that misconfiguration
 * used to be invisible.
 *
 * ⚠ `code` IS THE STORED IDENTITY (`au`/`us`, matching `ZoneCode` in cdk/lib/zones.ts and the
 * Lambdas' `ZONE_CODE`). `region` is a DISPLAY attribute only. Changing the code to the region
 * is ADR-123 Decision 5, pending Jeff — not something a label change may do by the back door.
 */
export interface ZoneDefinition {
  /** Wire value — matches `ZoneCode` in cdk/lib/zones.ts. */
  readonly code: string;
  /** The AWS region the zone runs in — what the console leads with. */
  readonly region: string;
  /** The legal jurisdiction — all a CUSTOMER is ever shown. */
  readonly jurisdiction: string;
  /** The country code a TABLE shows for the zone (Jeff, 31 October 2026 — `admin-portal-ui-standards.md` §6). */
  readonly country: string;
}

export const ZONE_TABLE: readonly ZoneDefinition[] = [
  { code: 'au', region: 'ap-southeast-2', jurisdiction: 'Australia', country: 'AU' },
  { code: 'us', region: 'us-east-1', jurisdiction: 'United States', country: 'US' },
];

/** How many zones the platform has — the "expected" in "1 zone configured — expected 2". */
export const EXPECTED_ZONE_COUNT = ZONE_TABLE.length;

/**
 * The ops console's name for a zone: region first — `us-east-1 · United States`.
 *
 * ⭐ REGION FIRST BECAUSE A COUNTRY CANNOT NAME A ZONE. A second US zone (`us-west-2`) is
 * plausible, and "United States" could not tell the two apart; the region can. Alarm emails
 * already lead with the region (ADR-120 Amendment 8), so this brings the console in line.
 * Resolved against the full table, not the configured subset, so an unconfigured zone is still
 * named properly in the indicator that reports it missing. An unknown code renders as itself —
 * a visible `zz` is a bug report; a blank is a mystery.
 */
export const consoleZoneLabel = (code: string): string => {
  const d = ZONE_TABLE.find((z) => z.code === code);
  return d ? `${d.region} · ${d.jurisdiction}` : code;
};

/**
 * What a TABLE shows for a zone: its country code — `AU`, `US` (Jeff, 31 October 2026: "we do not need the full zone
 * names at all on the table, just the abbreviated ones… if the full name needs to be exposed, make that a hover
 * tooltip"). The full name (`consoleZoneLabel`) is the tooltip. Only the zone filter and the "Working in" bar — pickers,
 * not tables — keep the full name. An unknown code renders as itself, upper-cased: visible, never blank.
 */
export const zoneCountryCode = (code: string): string =>
  ZONE_TABLE.find((z) => z.code === code)?.country ?? code.toUpperCase();

/** A table's label for a platform-wide row (an alert that is every zone's): `All`, full name on hover. */
export const PLATFORM_WIDE_CODE = 'All';
export const PLATFORM_WIDE_NAME = 'Platform-wide — every zone';

/**
 * A customer's name for a zone: jurisdiction only — `United States`. Customers care where their
 * data sits legally, not what AWS calls the datacentre (ADR-123 Decision 4). The staff console
 * uses it only where it quotes what a customer sees.
 */
export const customerZoneLabel = (code: string): string =>
  ZONE_TABLE.find((z) => z.code === code)?.jurisdiction ?? code;

export interface PortalZone extends ZoneDefinition {
  /** What an operator reads for this zone — `consoleZoneLabel(code)`, region first. */
  readonly label: string;
  /** This zone's admin-API origin. */
  readonly adminApiBaseUrl: string;
  /**
   * This zone's PORTAL-API origin, or `undefined` when the build was not given one.
   *
   * ⭐⭐ WHY A SECOND ORIGIN EXISTS AT ALL, AND WHY IT IS THE THING THAT MAKES "CREATE THIS
   * BETA/SUBSCRIBER ACCOUNT IN A CHOSEN ZONE" POSSIBLE.
   *
   * The staff OBW fork is a TWO-API journey and only the first half is admin-API. The mount
   * mints an HMAC launch token from `POST /admin/{bow,sow}/launch` (admin-API, zone-routable
   * since ADR-122), but the ACCOUNT IS CREATED at the other end, by
   * `POST /portal/{bow,sow}/provision` on PORTAL-api. Before this field that second call went
   * to one build-time constant — the home zone — so routing the MINT to us-east-1 would have
   * left the provision hitting ap-southeast-2. The record would have been born in AU while the
   * console said United States: strictly worse than today's honest home-zone default, because
   * a wrong answer that looks deliberate is harder to catch than no answer at all.
   *
   * ⚠ OPTIONAL ON PURPOSE, AND A ZONE WITHOUT ONE IS NOT OFFERED AS A CREATE TARGET. A zone
   * whose portal origin this build does not know is a zone whose OBW journey cannot complete,
   * so `OBW_CREATE_ZONES` below filters it out rather than letting the operator pick a target
   * that will dead-end at the completion step. That also makes the rollout safe in the one
   * order it can actually happen: this code ships before the deploy that exports the US portal
   * URL, and until then the control simply has one option, exactly as it does today.
   */
  readonly portalApiBaseUrl?: string;
  /** True for the zone whose bundle this is — the one that serves every non-list call. */
  readonly home: boolean;
}

const env = import.meta.env;

/**
 * ⚠⚠ THE HOME ZONE IS UNCONDITIONAL; ONLY *ADDITIONAL* ZONES ARE DROPPED WHEN UNCONFIGURED.
 * A first draft filtered on a missing URL uniformly, and the portal's own test suite caught
 * what that does: `VITE_ADMIN_API_BASE_URL` is unset under vitest, so `ZONES` came out EMPTY,
 * the fan-out ran zero reads, and every list rendered as "no customers" — a silent empty
 * console where the previous code would have attempted the call and surfaced a real error.
 *
 * That is the precise failure this module exists to prevent, reproduced in its own plumbing:
 * **a missing zone contributes no rows, and no rows is indistinguishable from none exist.** So
 * the home zone is always present and always attempted, exactly as before this change; a
 * SECOND zone with no URL is genuinely not a zone yet, and dropping it is correct — that is
 * local dev, every test, and any build made before us-east-1 existed.
 */
const configured = (u: string | undefined): u is string => typeof u === 'string' && u.length > 0;

export const ZONES: PortalZone[] = [
  // Always present, whatever its URL — a broken home URL must fail loudly, not silently.
  {
    ...ZONE_TABLE[0]!,
    label: consoleZoneLabel('au'),
    adminApiBaseUrl: env.VITE_ADMIN_API_BASE_URL,
    portalApiBaseUrl: env.VITE_PORTAL_API_BASE_URL,
    home: true,
  },
  ...(configured(env.VITE_ADMIN_API_BASE_URL_US)
    ? [{
        ...ZONE_TABLE[1]!,
        label: consoleZoneLabel('us'),
        adminApiBaseUrl: env.VITE_ADMIN_API_BASE_URL_US,
        // ⚠ ADMIN presence is what makes the zone exist; PORTAL presence is what makes it a
        // CREATE target. The two are separate `VITE_` exports and can legitimately arrive in
        // separate deploys, so they are read separately rather than gated on one another —
        // conflating them would drop the whole zone (and its fan-out reads) over a missing
        // portal URL.
        portalApiBaseUrl: configured(env.VITE_PORTAL_API_BASE_URL_US)
          ? env.VITE_PORTAL_API_BASE_URL_US
          : undefined,
        home: false,
      }]
    : []),
];

/**
 * The zone this bundle was built against — every single-record and mutating call goes here.
 * Guaranteed present: the home entry above is unconditional.
 */
export const HOME_ZONE: PortalZone = ZONES.find((z) => z.home)!;

/**
 * True when this bundle can reach more than one zone.
 *
 * ⚠ ADR-123 Decision 2 — THIS MAY GATE BEHAVIOUR, NEVER RENDERING. It used to decide whether
 * the Zone column, filter and switcher existed at all, which turned a deploy missing the US
 * address into a console that was AU-only and looked completely normal. Zone controls now
 * always render; in a one-zone bundle they render disabled, with the reason, and the
 * navigation bar says "1 zone configured — expected 2". Use this only to skip work that is
 * pointless with one zone — never to hide something.
 */
export const IS_MULTI_ZONE = ZONES.length > 1;

/** The visible reason a zone control is inert (ADR-123 Decision 2). */
export const ONE_ZONE_REASON = 'only one zone configured';

/**
 * The zones a staff-mounted OBW journey (BOW / SOW) may CREATE an account in.
 *
 * ⭐ A CREATE TARGET NEEDS BOTH ORIGINS, BECAUSE THE JOURNEY USES BOTH. The mint is admin-API
 * and the provision is portal-API (see `portalApiBaseUrl` above), so a zone missing either one
 * cannot carry a journey to completion. Offering it would let an operator choose a destination
 * that dead-ends at the final step, after they had entered the subscriber's details.
 *
 * ⚠ THIS IS NOT A PERMISSION CHECK — it is a capability check, and the server is unchanged by
 * it. Every zone's provision route remains reachable; this only decides what the console
 * OFFERS, which is the same division `isOperatorOrgNode` draws on the subscriber picker.
 */
export const OBW_CREATE_ZONES: PortalZone[] = ZONES.filter((z) => configured(z.portalApiBaseUrl));

/** True once more than one zone can host a staff-mounted OBW create — when the chooser earns
 *  its place on screen. */
export const CAN_CHOOSE_OBW_ZONE = OBW_CREATE_ZONES.length > 1;

/**
 * ── ADR-123 Decision 3 — the staff user's preferred zone ──────────────────────────────────────
 *
 * A module singleton, the same shape as `time-format`'s timezone preference: `useStaffMe` seeds
 * it from `GET /admin/me` and the profile page updates it on Save, and every zone control
 * re-renders through `useSyncExternalStore` without a context threaded through the tree.
 * `null` ⇒ unset ⇒ the platform home zone — exactly the console's behaviour before ADR-123.
 */
let preferredZone: string | null = null;
const preferenceListeners = new Set<() => void>();

export function setPreferredZone(code: string | null | undefined): void {
  const next = code && ZONE_TABLE.some((z) => z.code === code) ? code : null;
  if (next === preferredZone) return;
  preferredZone = next;
  preferenceListeners.forEach((l) => l());
}

export function getPreferredZone(): string | null {
  return preferredZone;
}

function subscribePreference(l: () => void): () => void {
  preferenceListeners.add(l);
  return () => preferenceListeners.delete(l);
}

/** React hook: the staff user's preferred zone code, or null for "platform default". */
export function usePreferredZone(): string | null {
  return useSyncExternalStore(subscribePreference, getPreferredZone, getPreferredZone);
}

/**
 * ── ADR-124 A2/A3 (amend ADR-123 Decisions 1 and 3) — the session's WORKING zone ────────────────
 *
 * ⭐⭐ "A STAFF MEMBER IS NEVER IN A ZONE WITHOUT HAVING BEEN TOLD SO." The preferred zone is a
 * standing choice made on My Profile; the working zone is what THIS session is doing now. It
 * starts as the preferred zone (else the platform home zone), the sign-in modal states it (A3),
 * and it changes only through the switch modal (A2) — an acknowledged act, never a side effect.
 *
 * It fills precedence level 3 of `resolveZonePreselection` — the level the preferred zone filled —
 * so ADR-123's ORDER is unchanged: `?zone=` → the page's own selection → the working zone (which
 * IS the preferred zone until the operator switches) → home. A switch never writes the
 * preference; changing the preference is still an explicit act on the profile page.
 *
 * ⚠ HELD IN sessionStorage, KEYED BY THE STAFF SUB. A tab refresh mid-session keeps the zone the
 * operator was told about — a refresh that silently dropped them back to their default would put
 * them in a zone nobody stated. A new sign-in clears it (`clearZoneSession`, called by the sign-in
 * page and by Sign Out), so the next session starts from the default and is told so again. A
 * second tab is a second session: Amplify keeps staff tokens per tab, and so does this.
 */
const WORKING_KEY = (sub: string) => `nexa.zone.working.${sub}`;
const ACK_KEY = (sub: string) => `nexa.zone.ack.${sub}`;
let sessionSub: string | null = null;
let workingZone: string | null = null;
let switchEpoch = 0;
const switchListeners = new Set<() => void>();

function storageGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function storageSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* private mode — the session simply re-prompts */ }
}

/** Bind the working-zone session to the signed-in staff member (their Cognito sub). */
export function bindZoneSession(sub: string | null | undefined): void {
  const next = sub || null;
  if (next === sessionSub) return;
  sessionSub = next;
  const stored = next ? storageGet(WORKING_KEY(next)) : null;
  workingZone = stored && ZONES.some((z) => z.code === stored) ? stored : null;
  preferenceListeners.forEach((l) => l());
}

/**
 * Apply a CONFIRMED switch (A2). Only the switch modal and the sign-in modal call this — never a
 * page control directly. Clears every page's own selection (precedence level 2), because the
 * modal has just told the operator that everything they view, edit or create targets this zone.
 */
export function setWorkingZone(code: string): void {
  if (!ZONES.some((z) => z.code === code)) return;
  workingZone = code;
  if (sessionSub) storageSet(WORKING_KEY(sessionSub), code);
  switchEpoch += 1;
  switchListeners.forEach((l) => l());
  preferenceListeners.forEach((l) => l());
}

/** The working zone code: the session's switched zone, else the preferred zone, else null (home). */
export function getWorkingZoneCode(): string | null {
  return workingZone ?? preferredZone;
}

/** React hook — the working zone code (null ⇒ platform home). Use where `usePreferredZone` fed a default. */
export function useWorkingZoneCode(): string | null {
  return useSyncExternalStore(subscribePreference, getWorkingZoneCode, getWorkingZoneCode);
}

/** React hook — the resolved working zone, never absent. */
export function useWorkingZone(): PortalZone {
  return resolveZonePreselection({ preferred: useWorkingZoneCode(), zones: ZONES });
}

function subscribeSwitch(l: () => void): () => void {
  switchListeners.add(l);
  return () => switchListeners.delete(l);
}
const getSwitchEpoch = () => switchEpoch;

/** React hook — increments on every confirmed switch; a page with its own selection resets on it. */
export function useZoneSwitchEpoch(): number {
  return useSyncExternalStore(subscribeSwitch, getSwitchEpoch, getSwitchEpoch);
}

/** Has this session already been told its zone (A3)? */
export function zoneSessionAcknowledged(): boolean {
  return !!sessionSub && storageGet(ACK_KEY(sessionSub)) === '1';
}

/** Record that the sign-in modal was acknowledged — once per session. */
export function acknowledgeZoneSession(): void {
  if (sessionSub) storageSet(ACK_KEY(sessionSub), '1');
}

/** Forget the working zone and the acknowledgement — a new sign-in starts again and is told again. */
export function clearZoneSession(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('nexa.zone.')) sessionStorage.removeItem(k);
    }
  } catch { /* nothing stored */ }
  workingZone = null;
  sessionSub = null;
  preferenceListeners.forEach((l) => l());
}

/**
 * ⭐⭐ THE ONE PRECEDENCE FUNCTION (ADR-123 Decision 3). Every zone default in the console comes
 * from here — the Pattern 3 switcher, every create target, and the zone a narrowed filter puts
 * first — so the rule cannot be re-implemented, slightly differently, per page.
 *
 * Highest first:
 *   1. an explicit `?zone=` in the URL — someone linked to a specific zone;
 *   2. the page's own selection this session — the operator already chose on this page;
 *   3. the staff user's preferred zone — their standing choice from My Profile;
 *   4. the platform home zone — the console's behaviour before any of this.
 *
 * Each candidate counts only if it is one of `zones` (the zones this control can offer): a
 * preference for a zone this bundle cannot reach, or that cannot host this create, falls
 * through to the next level rather than selecting something absent from the control.
 *
 * ⚠ A page-level selection NEVER writes the preference. The preference changes only on the
 * profile page — otherwise one switch on one page would silently re-home every other page.
 */
export function resolveZonePreselection({
  urlZone,
  pageSelection,
  preferred,
  zones = ZONES,
}: {
  urlZone?: string | null;
  pageSelection?: string | null;
  preferred?: string | null;
  zones?: readonly PortalZone[];
}): PortalZone {
  for (const code of [urlZone, pageSelection, preferred]) {
    const hit = code ? zones.find((z) => z.code === code) : undefined;
    if (hit) return hit;
  }
  // Level 4. `HOME_ZONE` is correct here and only here: it IS the platform default. If the home
  // zone cannot host this control's purpose, the first zone that can, never an absent one.
  return zones.find((z) => z.home) ?? zones[0] ?? HOME_ZONE;
}

/**
 * The zone a Pattern 1 filter should list first, so narrowing lands on the operator's own zone.
 * The filter itself still DEFAULTS to "All zones" (ADR-123 Decision 3) — a preference changes
 * which zone comes first when narrowing, never what a list shows by default.
 */
export function orderForPreselection(
  zones: readonly PortalZone[],
  preferred: string | null,
): PortalZone[] {
  const first = resolveZonePreselection({ preferred, zones });
  return [first, ...zones.filter((z) => z.code !== first.code)];
}

/**
 * Resolve a record's zone code to the zone to call.
 *
 * Falls back to the home zone for a record with no `zone` — a row that predates zone tagging,
 * or one fetched by a single-zone path. That is the correct default precisely because it is
 * the behaviour the console had before any of this existed.
 */
export const zoneByCode = (code: string | undefined): PortalZone | undefined =>
  (code ? ZONES.find((z) => z.code === code) : undefined) ?? HOME_ZONE;

/**
 * One zone's contribution to a fanned-out read: either rows, or the reason there are none.
 *
 * ⭐ A FAILED ZONE IS A DISTINCT OUTCOME, NEVER AN EMPTY LIST. If a zone's API is down and its
 * failure collapses into "no rows", the console shows a shorter list that looks authoritative —
 * an operator reads "this customer does not exist" when the truth is "one jurisdiction did not
 * answer". That is the worse failure of the two, because nothing about it looks wrong. The
 * caller is therefore handed the error and has to decide what to render.
 */
export interface ZoneResult<T> {
  readonly zone: PortalZone;
  readonly items: T[];
  readonly error?: unknown;
}

/**
 * Run `read` against every configured zone, in parallel, and return one result per zone —
 * including the ones that failed.
 *
 * `Promise.allSettled`, deliberately: with `Promise.all` a single unreachable zone rejects the
 * whole read and the operator sees an empty console instead of one jurisdiction's records plus
 * a warning. Partial truth, clearly labelled, beats no truth.
 */
export async function fanOutZones<T>(
  read: (zone: PortalZone) => Promise<T[]>,
): Promise<ZoneResult<T>[]> {
  const settled = await Promise.allSettled(ZONES.map((z) => read(z)));
  return settled.map((r, i) => {
    const zone = ZONES[i]!;
    return r.status === 'fulfilled'
      ? { zone, items: r.value }
      : { zone, items: [], error: r.reason };
  });
}

/** The zones that failed in a fan-out — what the UI needs to warn about, by console label. */
export const failedZoneLabels = <T>(results: ZoneResult<T>[]): string[] =>
  results.filter((r) => r.error).map((r) => r.zone.label);

/**
 * ⭐⭐ EVERY ZONE FAILING IS NOT A PARTIAL READ — IT IS A FAILED READ, AND MUST BE RE-THROWN.
 *
 * `fanOutZones` exists to stop ONE unreachable zone emptying the console, and it is right to
 * absorb that: the zones that answered still have something true to show, and the warning
 * names the one that did not. But the same absorption applied to a total failure is a
 * regression dressed as resilience — the page renders "no records" plus a warning, where
 * before it rendered its error state with a Retry. An empty list reads as an answer; an error
 * state reads as a question. On a review queue, "no applications" is exactly the sentence an
 * operator acts on by closing the tab.
 *
 * So a fanned-out reader calls this first and lets the original error propagate to the caller's
 * existing catch. The distinction is: at least one zone answered → partial, warn and render.
 * No zone answered → throw, exactly as the single-zone read it replaced would have.
 *
 * (Discovered 21 September 2026 by an existing regression test — `partners.test.tsx`'s "error
 * state renders only when every bucket fetch rejects" — which caught this the first time a
 * fan-out replaced a direct read on a page that HAD an error state to lose.)
 */
export function throwIfAllZonesFailed<T>(results: ZoneResult<T>[]): void {
  if (results.length > 0 && results.every((r) => r.error)) {
    throw results[0]!.error;
  }
}
